import { BehaviorSubject } from "rxjs/internal/BehaviorSubject"
import { RollingTimePeriodKind } from "src/app/core/services/ebr-api-client/ebr-api-client.g"

export type ServiceInsightsTableRow = {
    'totalLocations': number,
    'observationCount': number,
    'observationName': string,
    'implications': string[],
    'nextSteps': string[],
    'images': string,
    'addresses': string[]
    'checked':boolean
}

export type ServiceInsightsTable = {
    [key: string] : ServiceInsightsTableRow[]
}

export type ServiceInsightsPresentationDataObject = {
    "Finding":string,
    "Implications": string[],
    "NextSteps": string[]
}

export type ServiceInsightsPresentationData = {
    "addedOverallFindings": ServiceInsightsPresentationDataObject[],
    "addedIssuesFindings": ServiceInsightsPresentationDataObject[],
    "addedRecurringIssuesFindings": ServiceInsightsPresentationDataObject[]
}

export type ObservationsKpiData = {
    'observationCount': number,
    'observationName': string,
    'sortOrder': number
}

export type topBottomLocationRow = {
    'accountName': string ,
    'accountNumber': string ,
    'address':string ,
    'extraServiceCalls': number ,
    'vsAvg': number ,
    'issues': number ,
    'recurringIssues': number ,
    'highPrevIssue': string,
    'highPrevRecurringIssue': string
}

export interface GroupBy {
    initial: string,
    expanded: boolean
    isGroupBy: boolean
}

export type RegularServiceCallsTableRow = {
    'accountNumber': string,
    'unitNumber': string | null,
    'accountName': string,
    'shippingCity': string,
    'shippingState': string,
    'workOrderNumber': string,
    'callReason': string,
    'status': string,
    'endDate': string,
    'callSummaryNotes': string,
    'quickCallComments': string | null,
    'esrCallComments': string | null,
    'installationCallComments': string | null,
    'trainingCallComments': string | null,
    'observations': number
}

export type AddedServiceMetric = {
    'metricDataName': string,
    'metricDataValue': string
}

export type AddedTopBottomLocations = {
    'bottomFive': boolean,
    'bottomTen': boolean,
    'topFive': boolean,
    'topTen': boolean,
    'bottomTenPercent': boolean,
    'bottomFivePercent': boolean,
    'topTenPercent': boolean,
    'topFivePercent': boolean
}

export type ServiceInsightRecord = {
    'endDate': string,
    'total': number,
    'totalLocations': number
}

export type TotalCallsInsightModel = {
    'callReason': string,
    'total': number,
    'totalLocations': number
}

export type ByIssueMetrics = {
    'issueName': string,
    'callCount': number,
    'locations': number
}

export type ServiceInsightsByPeriodData = {
    periods : ServiceInsightRecord[],
    percentChangeInCount: number,
    distinctLocationsCount: number
}

export type ServiceInsightsMetricCount = {
    total: number,
    percentageChange: number | null,
    avgPerLocation: number | null,
    locations?: number
}

export type ServiceInsightsMetricCountsSummary = {
    callTypeCounts: Partial<Record<SICallTypes, ServiceInsightsMetricCount>>,
    trainingBreakdown: {
        trainingSessions: ServiceInsightsMetricCount,
        dedicatedTrainingCalls: ServiceInsightsMetricCount,
        trainingTopics: ServiceInsightsMetricCount,
        inServiceTraining: ServiceInsightsMetricCount
    }
}

export enum ServiceMetricTypes
{
    Calls = "Calls",
    Observations = "Observations",
    Services = "Services", // Pest division specific
    Findings = "Findings", // Pest division specific
    CallsByReason = "CallsByReason" // Pest division specific
}

// TODO: SICallTypes no longer accurately reflects the enum contents.
// The enum contains Service Insights metrics, findings, work order types,
// and call categories. Rename to a more representative domain name.
export enum SICallTypes {
    UntrackedCalls = "UntrackedCalls", // Call types that we don't care about as of now at SI level.
    TotalCalls = "TotalCalls", // Encompasses all call types tracked at SI level

    // Institutional/QSR/FRS Workorder types
    PhotoObservations = "PhotoObservations",
    VirtualAssistCalls = "VirtualAssistCalls",

    ExtraServiceVisit = "ExtraServiceVisit",
    RegularServiceCalls = "RegularServiceCalls",
    ProgramReviews = "ProgramReviews",
    Installations = "Installations",
    QuickCalls = "QuickCalls",
    TrainingSessions = "TrainingSessions", // training calls and calls that contain in service-training observations

    //FRS/QSR calls have AUDITs in place of Program Reviews
    Audit = "Audit",

    // Pest Services
    ContractedCleanOut = "ContractedCleanOut",
    ContractedRegular = "ContractedRegular",
    ExtraOneShot = "ExtraOneShot",
    ExtraFollowUp = "ExtraFollowUp",

    // Pest Findings
    Pest = "Pest",
    Sanitation = "Sanitation",
    Structural = "Structural",
    Prep = "Prep",

    // Pest Calls By Reason
    ExtraServiceRequest = "ExtraServiceRequest",
}

export type ServiceInsightsCallData = {
    [SICallTypes.ExtraServiceVisit] : BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.RegularServiceCalls] : BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.ProgramReviews]: BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.Installations]: BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.TrainingSessions]: BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.QuickCalls]: BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.TotalCalls]: BehaviorSubject<ServiceInsightsByPeriodData>,
    // Only for QSR/ FRS
    [SICallTypes.Audit]: BehaviorSubject<ServiceInsightsByPeriodData>,
}

export type ServiceInsightsObservationsData = {
    [SICallTypes.PhotoObservations]: BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.VirtualAssistCalls]: BehaviorSubject<ServiceInsightsByPeriodData>,
}

export type PestServiceInsightsCallData = {
  [SICallTypes.ContractedCleanOut] : BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.ContractedRegular] : BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.ExtraOneShot]: BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.ExtraFollowUp]: BehaviorSubject<ServiceInsightsByPeriodData>,
  "Services-total-calls": BehaviorSubject<ServiceInsightsByPeriodData>,
  "Findings-total-calls": BehaviorSubject<ServiceInsightsByPeriodData>,
  "CallsByReason-total-calls": BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.Pest]:BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.Sanitation]:BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.Structural]:BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.Prep]:BehaviorSubject<ServiceInsightsByPeriodData>,
  [SICallTypes.ExtraServiceRequest]: BehaviorSubject<ServiceInsightsByPeriodData>,
}

export type ServiceInsightsByLocationData = {
    [SICallTypes.ExtraServiceVisit] : BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.RegularServiceCalls] : BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.ProgramReviews] : BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.Installations] : BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.TrainingSessions] : BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.QuickCalls] : BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.VirtualAssistCalls]: BehaviorSubject<ByLocationMetrics[]>,
    [SICallTypes.TotalCalls]: BehaviorSubject<ByLocationMetrics[]>,
    // Only for QSR/ FRS
    [SICallTypes.Audit] : BehaviorSubject<ByLocationMetrics[]>,
}

export type ServiceInsightsObsByLocationData = {
    [SICallTypes.PhotoObservations]: BehaviorSubject<ServiceInsightsByPeriodData>,
    [SICallTypes.VirtualAssistCalls]: BehaviorSubject<ServiceInsightsByPeriodData>,
}

export type ServiceInsightsByIssueData = {
    [SICallTypes.ExtraServiceVisit] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.RegularServiceCalls] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.ProgramReviews] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.Installations] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.TrainingSessions] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.QuickCalls] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.VirtualAssistCalls] : BehaviorSubject<ByIssueMetrics[]>,
    [SICallTypes.TotalCalls] : BehaviorSubject<ByIssueMetrics[]>,
    // Only for QSR/ FRS
    [SICallTypes.Audit] : BehaviorSubject<ByIssueMetrics[]>,
}

export type SITotalCallsByType = {
    totalCalls : TotalCallsInsightModel[]
}

export type PestTotalCallsByTypeData = {
    "Services-total-calls": BehaviorSubject<SITotalCallsByType>,
    "Findings-total-calls": BehaviorSubject<SITotalCallsByType>,
    "CallsByReason-total-calls": BehaviorSubject<SITotalCallsByType>
}


export const SICallTypesDisplayName: { [key in SICallTypes]: string } = {
    [SICallTypes.UntrackedCalls] : "",
    [SICallTypes.TotalCalls] :"",

    // Institutional/QSR/FRS Workorder types
    [SICallTypes.PhotoObservations] : "Photo Observations",
    [SICallTypes.VirtualAssistCalls] : "Virtual Assist Calls",
    [SICallTypes.ExtraServiceVisit] : "Extra Service Visits",
    [SICallTypes.RegularServiceCalls] : "Regular Service Calls",
    [SICallTypes.ProgramReviews] : "Program Reviews",
    [SICallTypes.Installations] : "Installations",
    [SICallTypes.QuickCalls] : "Quick Calls",
    [SICallTypes.TrainingSessions] : "Training Sessions",
    [SICallTypes.Audit] : "Audits",

    // Pest Services
    [SICallTypes.ContractedCleanOut] : "Contracted - Clean Out",
    [SICallTypes.ContractedRegular] : "Contracted - Regular",
    [SICallTypes.ExtraOneShot] : "Extra - One Shot",
    [SICallTypes.ExtraFollowUp] : "Extra - Follow Up",

    // Pest Findings
    [SICallTypes.Pest] : "Pest",
    [SICallTypes.Sanitation] : "Sanitation",
    [SICallTypes.Structural] : "Structural",
    [SICallTypes.Prep] : "Prep",

    // Pest Calls By Reason
    [SICallTypes.ExtraServiceRequest] : "Pest ESR",
}

export const SICallTypesDatabaseCode: { [key in SICallTypes]: string } = {
    [SICallTypes.UntrackedCalls] : "",
    [SICallTypes.TotalCalls] :"",

    // Institutional/QSR/FRS Workorder types
    [SICallTypes.PhotoObservations] : "PHOTOS",
    [SICallTypes.VirtualAssistCalls] : "",
    [SICallTypes.ExtraServiceVisit] : "ESR",
    [SICallTypes.RegularServiceCalls] : "RSC",
    [SICallTypes.ProgramReviews] : "PRC",
    [SICallTypes.Installations] : "INST",
    [SICallTypes.QuickCalls] : "Quick Call",
    [SICallTypes.TrainingSessions] : "TRN",
    [SICallTypes.Audit] : "AUDIT",

    // Pest Services
    [SICallTypes.ContractedCleanOut] : "Contracted - Clean Out",
    [SICallTypes.ContractedRegular] : "Contracted - Regular",
    [SICallTypes.ExtraOneShot] : "Extra - One Shot",
    [SICallTypes.ExtraFollowUp] : "Extra - Follow-up",

    // Pest Findings
    [SICallTypes.Pest] : "PEST",
    [SICallTypes.Sanitation] : "SANITATION",
    [SICallTypes.Structural] : "STRUCTURAL",
    [SICallTypes.Prep] : "PREP",

    // Pest Calls By Reason
    [SICallTypes.ExtraServiceRequest] : "",
}


export const SICallTypesColorCode: { [key in SICallTypes]: string } = {
    [SICallTypes.UntrackedCalls] : "#000000",
    [SICallTypes.TotalCalls] :"#000000",

    // Institutional/QSR/FRS Workorder types
    [SICallTypes.PhotoObservations] : "#D3D3D3",
    [SICallTypes.VirtualAssistCalls] : "#EBA68F",
    [SICallTypes.ExtraServiceVisit] : "#3290ED",
    [SICallTypes.RegularServiceCalls] : "#39b54a",
    [SICallTypes.ProgramReviews] : "#9D53F2",
    [SICallTypes.Installations] : "#6DD0E5",
    [SICallTypes.QuickCalls] : "#ECC42F",
    [SICallTypes.TrainingSessions] : "#77B5F2",
    [SICallTypes.Audit] : "#9D53F2",

    // Pest Services
    [SICallTypes.ContractedCleanOut] : "#1aa5e0",
    [SICallTypes.ContractedRegular] : "#39b54a",
    [SICallTypes.ExtraOneShot] : "#4fc2b0",
    [SICallTypes.ExtraFollowUp] : "#ecc42f",

    // Pest Findings
    [SICallTypes.Pest] : "#1aa5e0",
    [SICallTypes.Sanitation] : "#4fc2b0",
    [SICallTypes.Structural] : "#39b54a",
    [SICallTypes.Prep] : "#ecc42f",

    // Pest Calls By Reason
    [SICallTypes.ExtraServiceRequest] : "#006bd3",
}


export type CachedExpandedDetails = {
    'pageNumber': number,
    'expandedDetailsJson': string,
}

export type ByLocationMetrics = {
 accountNumber: string,
 location: string,
 recurringIssues: number,
 totalCalls: number
}

export type PhotoInfo = {
    workOrderId: string,
    photoUrl: string,
    description:string,
    ecolabRepName:string,
    endDate: string,
    customerName:string,
    accountNumber: string,
    callReasonCode: string,
}

export type GroupedPhotos = {
    countOfCalls: number,
    sortBy: string,
    photos: PhotoInfo[]
}

export type ExpandedPhotosResult = {
    count: number;
    totalCount: number;
    photos: GroupedPhotos[]
}

export type ExpandedPhotosByGroupKey = 'endDate' | 'location' | 'issue';

export enum KpiTypes {
    ServiceKPI = 'Service',
    CallsByReasonKPI = 'CallsByReason',
    ScanCountKPI = 'Scan Pest Count', // Pest division specific
    // TODO : Add other KPIs for Pest
}

export interface ServiceMetric {
  key: SICallTypes;
  title: string;
  borderColor: string;
}

export interface FindingMetric {
  key: SICallTypes;
  title: string;
  borderColor: string;
}

export interface CallMetric {
  key: SICallTypes;
  title: string;
  borderColor: string;
}

export interface ServiceKpiCard {
  key: string;
  title: string;
  value: number | string;
  icon: string;
}


export type ExpandedPhotosMap = Record<number, ExpandedPhotosResult>;

export const SICallTypeToServiceMetricType: { [key in SICallTypes]: ServiceMetricTypes } = {
    // Observations
    [SICallTypes.PhotoObservations]: ServiceMetricTypes.Observations,
    [SICallTypes.VirtualAssistCalls]: ServiceMetricTypes.Observations,
    // Calls
    [SICallTypes.ExtraServiceVisit]: ServiceMetricTypes.Calls,
    [SICallTypes.RegularServiceCalls]: ServiceMetricTypes.Calls,
    [SICallTypes.ProgramReviews]: ServiceMetricTypes.Calls,
    [SICallTypes.Installations]: ServiceMetricTypes.Calls,
    [SICallTypes.QuickCalls]: ServiceMetricTypes.Calls,
    [SICallTypes.TrainingSessions]: ServiceMetricTypes.Calls,
    [SICallTypes.Audit]: ServiceMetricTypes.Calls,
    [SICallTypes.TotalCalls]: ServiceMetricTypes.Calls,
    [SICallTypes.UntrackedCalls]: ServiceMetricTypes.Calls,

    // Pest
    [SICallTypes.ContractedCleanOut]: ServiceMetricTypes.Services,
    [SICallTypes.ContractedRegular]: ServiceMetricTypes.Services,
    [SICallTypes.ExtraOneShot]: ServiceMetricTypes.Services,
    [SICallTypes.ExtraFollowUp]: ServiceMetricTypes.Services,
    [SICallTypes.Pest]: ServiceMetricTypes.Findings,
    [SICallTypes.Sanitation]: ServiceMetricTypes.Findings,
    [SICallTypes.Structural]: ServiceMetricTypes.Findings,
    [SICallTypes.Prep]: ServiceMetricTypes.Findings,
    [SICallTypes.ExtraServiceRequest]: ServiceMetricTypes.CallsByReason,
};

export interface ServiceInsightsDetailsKpiCardsData {
    period: RollingTimePeriodKind,
    metricTotal: number | null,
    percentageChange: number | null,
}
